'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { UiReviewGalleryBase } from '@/fo/ui/review/gallery/base';
import { UiReviewTabsBase, ReviewTab } from '@/fo/ui/review/tabs/base';

import { Layout, QuestionsList, ReviewsList, Sidebar } from '../components';
import { StickyHeader } from '../components/StickyHeader/StickyHeader';
import { FilterSyncProvider } from '../hooks/FilterSyncContext';
import {
  Product,
  ReviewsFilter,
  ReviewsData,
  QuestionsData,
  SortType
} from '../types';

import { styles } from './UiReviewCatalogBase.styles';

const DEFAULT_FILTERS: ReviewsFilter = {
  selectedRating: null,
  selectedTags: []
};

export interface UiReviewCatalogBaseProps {
  reviewsData: ReviewsData;
  questionsData: QuestionsData;
  product: Product;
  onActionClick?: (tab: ReviewTab) => void;
  onImageClick?: (imageId: string) => void;
  filters?: ReviewsFilter;
  onFiltersChange?: (filters: ReviewsFilter) => void;
  onSortTypeChange?: (sortType: SortType | null) => void;
  onLoadMore?: (tab: ReviewTab) => void;
  onClose?: () => void;
  isReviewsLoading?: boolean;
  isQuestionsLoading?: boolean;
  reviewsShowLoadMore?: boolean;
  activeTab?: ReviewTab;
}

export function UiReviewCatalogBase({
  reviewsData,
  questionsData,
  product,
  onActionClick,
  onImageClick,
  onLoadMore,
  onClose,
  isReviewsLoading,
  isQuestionsLoading,
  onFiltersChange,
  onSortTypeChange,
  reviewsShowLoadMore,
  activeTab: initialActiveTab,
  ...props
}: UiReviewCatalogBaseProps) {
  const t = useTranslations();
  const slots = styles();

  const [activeTab, setActiveTab] = useState<ReviewTab>(
    initialActiveTab ?? ReviewTab.Reviews
  );

  const [sortType, setSortType] = useState<SortType | null>(null);

  const [filters, setFilters] = useState<ReviewsFilter>(
    props.filters ?? DEFAULT_FILTERS
  );

  const handleFiltersChange = (filters: ReviewsFilter) => {
    setFilters(filters);
    onFiltersChange?.(filters);
  };

  const handleSortTypeChange = (sortType: SortType | null) => {
    setSortType(sortType);
    onSortTypeChange?.(sortType);
  };

  const tabs = [
    {
      id: ReviewTab.Reviews,
      title: t('review.tabs.reviews'),
      emptyTitle: t('review.empty.reviews'),
      count: reviewsData.totalCount
    },
    {
      id: ReviewTab.Questions,
      title: t('review.tabs.questions'),
      emptyTitle: t('review.empty.questions'),
      count: questionsData.totalCount
    }
  ];

  const main =
    activeTab === ReviewTab.Reviews ? (
      <ReviewsList
        reviews={reviewsData.items}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onLoadMore={() => onLoadMore?.(ReviewTab.Reviews)}
        isShowLoadMore={reviewsShowLoadMore}
        isLoading={isReviewsLoading}
      />
    ) : (
      <QuestionsList
        questions={questionsData.items}
        sortType={sortType}
        onSortTypeChange={handleSortTypeChange}
        onLoadMore={() => onLoadMore?.(ReviewTab.Questions)}
        isShowLoadMore={questionsData.items.length < questionsData.totalCount}
        isLoading={isQuestionsLoading}
      />
    );

  const sidebar = (
    <Sidebar
      reviewsData={reviewsData}
      product={product}
      activeTab={activeTab}
      onActionClick={onActionClick}
      filters={filters}
      onChangeFilters={handleFiltersChange}
    />
  );

  return (
    <FilterSyncProvider>
      <Layout
        stickyHeader={
          <StickyHeader
            tabs={tabs}
            activeTabId={activeTab}
            onTabChange={setActiveTab}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            sortType={sortType}
            onSortTypeChange={handleSortTypeChange}
            onClose={onClose}
          />
        }
        header={
          <>
            <UiReviewTabsBase
              tabs={tabs}
              activeTabId={activeTab}
              onTabChange={setActiveTab}
            />
            {activeTab === ReviewTab.Reviews && (
              <div className={slots.gallery()}>
                <UiReviewGalleryBase
                  images={reviewsData.images}
                  onImageClick={onImageClick}
                />
              </div>
            )}
          </>
        }
        sidebar={sidebar}
      >
        {main}
      </Layout>
    </FilterSyncProvider>
  );
}
